import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useReducer, useState, useRef, useMemo, useCallback } from 'react';
import { apiRequest } from '../../../services/utils/HttpUtils';
import EditIcon from '@mui/icons-material/Edit';
import { useMsal } from '@azure/msal-react';
import { Assumption } from '../TableData';
import { getAllLists } from '../../../graphql/queries_pipelineEditor';
import { Box, Button, Grid, ListItemIcon, MenuItem } from '@mui/material';
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { MRT_Cell, MRT_ColumnDef, MRT_FullScreenToggleButton, MRT_RowSelectionState, MRT_TableInstance, MaterialReactTable, MaterialReactTableProps } from 'material-react-table';
import { assumptionEditorColumn } from '../componentsConfig/assumptionEdiorConfig';
import { assumptionIntro } from '../../../assets/texts/pageTextContent';
import CreateNewAssumptionModal from './CreateNewAssumptionModal';
import { CUSTOM_ASSUMPTION_DELETE, CUSTOM_ASSUMPTION_MUTATE, CUSTOM_ASSUMPTION_QUERY } from '../../../graphql/queries_custom_assumptions';
import { UpsertAssumptionRecord } from '../../../services/assumptionEdiorService/UpsertAssumptionRecord';
import useBaseLineAssumption from '../../../hooks/useBaseLineAssumption';
import {getUserInfo} from "../../../graphql/queries_user_info";

type Props = {
    scenarioID: string
    onTabChange: (index: number) => void
}



const ProjectPhasingV2 = (props: Props) => {
    const { scenarioID, onTabChange } = props
    const client = useQueryClient();
    const {data: userInfo} = getUserInfo();
    const { instance, accounts } = useMsal()
    const rerender = useReducer(() => ({}), {})[1];
    // State
    const [phasingData, setPhasingData] = useState<Assumption[]>([{
        ProjectTypeID: "",
        RawProjectType: "Baseline",
        PlanningTimePercentage: 15,
        ConstructionTimePercentage: 80,
        CommissioningTimePercentage: 5,
        PlanningCostPercentage: 31,
        ConstructionCostPercentage: 59,
        CommissioningCostPercentage: 10
    }]);
    const [reset, setReset] = useState(0);
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
    const tableInstanceRef = useRef<MRT_TableInstance<Assumption>>(null);
    const [validationErrors, setValidationErrors] = useState<{ [cellId: string]: string; }>({});
    const [isAddAssumptionModalOpen, setIsAddAssumptionModalOpen] = useState(false);


    // React Query
    const { baseLineAssumption, isBaseLineAssumptionSuccess } = useBaseLineAssumption({ scenarioID, instance, accounts });

    const { data: AllLists } = useQuery(
        {
            queryKey: ["Lists"],
            queryFn: () => {
                return apiRequest(
                    {
                        query: getAllLists,
                        variables: {
                            // @ts-ignore
                            inJurisdiction: userInfo?.executeGetUser[0].Jurisdiction ?? "",
                            inScenarioID: ""
                        },
                        instance: instance,
                        accounts: accounts
                    }
                )
            },
            cacheTime: Infinity,
            enabled: !!userInfo
        });

    const { data: scenarioAssumption, isLoading, isFetching, isSuccess } = useQuery(
        ["getScenarioAssumptions"], {
        queryFn: () => {
            return apiRequest(
                {
                    query: CUSTOM_ASSUMPTION_QUERY,
                    variables: {
                        inScenarioID: scenarioID,
                        inProjectTypeID: 0
                    },
                    instance: instance,
                    accounts: accounts
                })
        }
    });

    const updateScenarioAssumptions = useMutation(
        ["updateScenarioAssumptions"], {
        mutationFn: (variables: any) => {
            return apiRequest(
                {
                    query: CUSTOM_ASSUMPTION_MUTATE,
                    variables: variables,
                    instance: instance,
                    accounts: accounts
                })
        },
        onSuccess: (data) => {
            setReset(0);
            client.invalidateQueries(["getScenarioAssumptions"]);
        },
    });

    const deleteScenarioAssumptions = useMutation(
        ["deleteScenarioAssumptions"], {
        mutationFn: (variables: any) => {
            return apiRequest(
                {
                    query: CUSTOM_ASSUMPTION_DELETE,
                    variables: variables,
                    instance: instance,
                    accounts: accounts
                })
        },
        onSuccess: (data) => {
            setReset(0);
            client.invalidateQueries(["getScenarioAssumptions"]);
        },
    });

    // Event Handler
    const handleSaveRow: MaterialReactTableProps<Assumption>["onEditingRowSave"] =
        async ({ exitEditingMode, row, values }) => {
            const timeTotalCellId = row._getAllCellsByColumnId()["TimeTotal"].id
            const costTotalCellId = row._getAllCellsByColumnId()["CostTotal"].id
            const timeTotalValue = parseInt(values["PlanningTimePercentage"]) + parseInt(values["ConstructionTimePercentage"]) + parseInt(values["CommissioningTimePercentage"])
            const costTotalValue = parseInt(values["PlanningCostPercentage"]) + parseInt(values["ConstructionCostPercentage"]) + parseInt(values["CommissioningCostPercentage"])
            //send/receive api updates here
            // validation
            if (timeTotalValue !== 100) {
                setValidationErrors({
                    ...validationErrors,
                    [timeTotalCellId]: "Total time percentage must be equal to 100%",
                });
                return;
            } else if (timeTotalValue === 100) {
                //remove validation error for cell if valid
                delete validationErrors[timeTotalCellId];
                setValidationErrors({
                    ...validationErrors,
                });
            }

            if (costTotalValue !== 100) {
                setValidationErrors({
                    ...validationErrors,
                    [costTotalCellId]: "Total Investment percentage must be equal to 100%",
                });
                return;
            } else if (costTotalValue === 100) {
                //remove validation error for cell if valid
                delete validationErrors[costTotalCellId];
                setValidationErrors({
                    ...validationErrors,
                });
            }

            // optimistic update
            values.ProjectTypeID = row.original.ProjectTypeID;
            phasingData[row.index] = values;
            setPhasingData([...phasingData]);

            // send api request to update assumption
            updateScenarioAssumptions.mutate(
                UpsertAssumptionRecord(scenarioID, "editing", values)
            )


            exitEditingMode()

        };

    const handleCreateNewRowModal = (values: Assumption) => {
        // send api request to create new assumption
        updateScenarioAssumptions.mutate(
            UpsertAssumptionRecord(scenarioID, "creating", values)
        )
    }


    // Side effect code
    useEffect(() => {

        if (isSuccess && isBaseLineAssumptionSuccess) {
            // @ts-ignore
            const tempBaseLineAssumption = baseLineAssumption?.executeGetScenarioAssumption.map((assumption: any) => {
                return {
                    ProjectTypeID: assumption.ProjectTypeID || "",
                    // @ts-ignore
                    RawProjectType: "Baseline",
                    PlanningTimePercentage: assumption.Planning * 100,
                    ConstructionTimePercentage: assumption.Construction * 100,
                    CommissioningTimePercentage: assumption.Commissioning * 100,
                    PlanningCostPercentage: assumption.PlanningCostPercentage * 100,
                    ConstructionCostPercentage: assumption.ConstructionCostPercentage * 100,
                    CommissioningCostPercentage: assumption.CommissioningCostPercentage * 100,
                }
            })
            // @ts-ignore
            const tempPhasingData = scenarioAssumption?.executeGetScenarioCustomAssumption.map((assumption: any) => {
                return {
                    ProjectTypeID: assumption.ProjectTypeID || "",
                    // @ts-ignore
                    RawProjectType: AllLists?.executeGetRawProjectTypes.find((project: any) => project.RawProjectTypeID === assumption.ProjectTypeID)?.RawProjectType || "",
                    PlanningTimePercentage: assumption.TimePlanning * 100,
                    ConstructionTimePercentage: assumption.TimeConstruction * 100,
                    CommissioningTimePercentage: assumption.TimeCommissioning * 100,
                    PlanningCostPercentage: assumption.InvestmentPlanning * 100,
                    ConstructionCostPercentage: assumption.InvestmentConstruction * 100,
                    CommissioningCostPercentage: assumption.InvestmentCommissioning * 100,
                }
            }
            )
            setPhasingData([...tempBaseLineAssumption, ...tempPhasingData]);
        }
    }, [isSuccess, scenarioAssumption, reset, baseLineAssumption])

    const getCommonEditTextFieldProps = useCallback(
        (
            cell: MRT_Cell<Assumption>,
        ): MRT_ColumnDef<Assumption>['muiTableBodyCellEditTextFieldProps'] => {
            return {
                error: !!validationErrors[cell.id],
                helperText: validationErrors[cell.id],
            };
        },
        [validationErrors],
    );

    const assumptionColumns = useMemo(() => assumptionEditorColumn(AllLists, getCommonEditTextFieldProps), [AllLists, getCommonEditTextFieldProps])


    return (
        <>
            <Box p={1}>
                <Grid container spacing={1}>
                    <Grid item xs={12} >{assumptionIntro}</Grid>
                    <Grid item >
                        <Button
                            onClick={() => { setIsAddAssumptionModalOpen(true) }}
                            variant="outlined"
                            sx={{
                                borderRadius: "35px",
                            }}
                        >
                            Add an assumption
                            <AddIcon />
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <MaterialReactTable
                // @ts-ignore
                columns={assumptionColumns}
                data={phasingData ?? []}
                enableRowActions
                enableRowVirtualization
                rowVirtualizerProps={{ overscan: 10 }}
                enablePagination={false}
                // Issue with Material table conditional editing (v1 1.14), dummy condition need to be in here
                // so the editing row state is managed properly internally
                enableEditing={(row) => row.index % 1 === 0 && row.index !== 0}
                onEditingRowCancel={() => { setValidationErrors({}) }}
                enableSorting={true}
                globalFilterFn="includesString"
                editingMode="row"
                enableFacetedValues
                enableClickToCopy
                enableColumnResizing
                enableColumnActions={false}
                enableTopToolbar
                positionGlobalFilter="left"
                onEditingRowSave={handleSaveRow}
                enableStickyHeader
                muiTableContainerProps={{ sx: { maxHeight: "40vh" } }}
                onRowSelectionChange={(updater) => {
                    setRowSelection((prev) =>
                        updater instanceof Function ? updater(prev) : updater,
                    );
                    queueMicrotask(rerender); //hack to rerender after state update
                }}
                initialState={{
                    showGlobalFilter: true,
                    density: "compact"
                }}
                state={{
                    rowSelection,
                    isLoading,
                    showProgressBars: isFetching,
                    columnPinning: {
                        right: [],
                        left: ['mrt-row-actions']
                    }
                }}
                tableInstanceRef={tableInstanceRef}
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        Header: "", //change header text
                        size: 100, //make actions column smaller
                    },
                }}
                positionToolbarAlertBanner="top"
                muiTableBodyProps={{
                    sx: () => ({
                        "& tr:nth-of-type(odd)": {
                            backgroundColor: "#8578741A",
                        },
                    })
                }}
                positionActionsColumn="first"
                renderRowActionMenuItems={({ closeMenu, row, table }) => {
                    if (row.index !== 0) {
                        return [
                            // Removed the "Edit" MenuItem
                            <MenuItem
                                key={1}
                                onClick={() => {
                                    // TODO: Implement delete assumption
                                    deleteScenarioAssumptions.mutate(UpsertAssumptionRecord(scenarioID, "deleting", { ProjectTypeID: row.original.ProjectTypeID }))
                                    closeMenu();
                                }}
                                sx={{ m: 0 }}
                            >
                                <ListItemIcon>
                                    <DeleteForeverRoundedIcon />
                                </ListItemIcon>
                                Delete
                            </MenuItem>,
                            <MenuItem
                                key={2}
                                onClick={() => {
                                    // TODO: Implement restore assumption
                                    closeMenu();
                                }}
                                sx={{ m: 0 }}
                                disabled
                            >
                                <ListItemIcon>
                                    <ReplayRoundedIcon />
                                </ListItemIcon>
                                Restore
                            </MenuItem>,
                        ]
                    } else {
                        closeMenu();
                        return []
                    }
                }}
                renderToolbarInternalActions={({ table }) => (
                    // render an empty action are to disable the duplicated actions(full screen etc.)
                    <Box>
                        <MRT_FullScreenToggleButton table={table} />
                    </Box>
                )}
                renderBottomToolbarCustomActions={({ }) => {
                    return (
                        <Box
                            sx={{ display: "flex", gap: "0.5rem", p: 2, justifyContent: "flex-end", width: "100%" }}
                        >

                            <Button
                                onClick={() => onTabChange(1)}
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                            >
                                Back
                            </Button>
                            <Button
                                onClick={() => onTabChange(3)}
                                variant="contained"
                            >
                                Generate scenario
                            </Button>

                        </Box>
                    );

                }}
            />
            <CreateNewAssumptionModal
                // @ts-ignore
                open={isAddAssumptionModalOpen}
                onClose={() => setIsAddAssumptionModalOpen(false)}
                onSubmit={handleCreateNewRowModal}
                AllLists={AllLists}
            />
        </>
    );
}

export default ProjectPhasingV2;
